import {
  BarChart,
  Bar,
  ResponsiveContainer,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Label,
} from "recharts";
import { useEffect, useMemo, useState } from "react";

import { Tab } from "../../../components/tab";
import { DashboardChartPoint } from "./types";
import { useAppDispatch, useAppSelector } from "../../../hooks/hooks";
import {
  DashboardUserActivityPeriod,
  fetchDashboardTransactionActivity,
  selectDashboardTransactionActivity,
} from "../../../features/auth/store/dashboardStatSlice";

const BarChartComp = ({ data }: { data: DashboardChartPoint[] }) => {
  return (
    <div className="w-full h-[200px] sm:h-64">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data}>
          <CartesianGrid strokeDasharray="3 3" vertical={false} />
          <XAxis dataKey="name" />
          <YAxis width={35}>
            <Label
              value="Transactions"
              angle={-90}
              position="insideLeft"
              className="hidden sm:block"
              style={{ textAnchor: "middle" }}
            />
          </YAxis>
          <Tooltip />
          <Bar
            dataKey="value"
            fill="#DC6803"
            radius={[8, 8, 0, 0]}
            barSize={24}
          />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

export const TransactionsCount = () => {
  const dispatch = useAppDispatch();
  const [activeIndex, setActiveIndex] = useState(1);

  const periods: {
    label: string;
    value: DashboardUserActivityPeriod;
  }[] = useMemo(
    () => [
      { label: "12 months", value: "12_months" },
      { label: "30 days", value: "30_days" },
      { label: "7 days", value: "7_days" },
      { label: "24 hours", value: "24_hours" },
    ],
    [],
  );

  const activePeriod = periods[activeIndex]?.value ?? "30_days";
  const transactionActivity = useAppSelector(
    selectDashboardTransactionActivity(activePeriod),
  );

  useEffect(() => {
    if (!transactionActivity.lastUpdated && !transactionActivity.loading) {
      dispatch(fetchDashboardTransactionActivity(activePeriod));
    }
  }, [activePeriod, dispatch]);

  const tabs = periods.map((period) => ({
    label: period.label,
    content: transactionActivity.loading ? (
      <div className="flex h-[200px] sm:h-64 items-center justify-center">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500"></div>
      </div>
    ) : transactionActivity.error ? (
      <div className="flex h-[200px] sm:h-64 items-center justify-center text-sm text-red-500">
        Failed to load transaction activity
      </div>
    ) : (
      <BarChartComp data={transactionActivity.data} />
    ),
  }));

  return (
    <div className="rounded-xl border border-gray-200 p-4 sm:p-6 flex flex-col gap-6 w-full">
      <div className="flex flex-col gap-5 w-full">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between w-full">
          <span className="satoshi-xl-bold-pry-900 dark:text-white">
            Transactions Count
          </span>
          <button className="rounded-lg border border-gray-300 py-2 px-3 satoshi-sm-bold-sec-900 dark:text-white text-center w-full sm:w-auto text-sm">
            View report
          </button>
        </div>

        <Tab
          initialActiveIndex={1}
          options={tabs}
          onChange={setActiveIndex}
          className="w-full flex flex-col gap-5"
          tabListClassName="border-b border-gray-200 flex items-center gap-2 w-full overflow-x-auto scrollbar-none whitespace-nowrap"
          tabItemClassName="px-2 pb-2 pt-[1px] satoshi-xs-bold-gray-400 flex-shrink-0"
          activeTabItemClassName="border-b-2 border-pry-700"
        />
      </div>
    </div>
  );
};
